/* Why a page scored what it scored.

   One component for both screens. The one-page panel and the worksheet were
   showing different amounts of the same stored reading — the scan captures the
   axes, the reasons, the quoted lines and the mechanical facts either way, and
   the worksheet simply was not rendering them. Two renderings of one record
   drift, and then somebody argues with a number using a screen that is not
   showing them the same thing.

   The quoted generic lines are the actionable half: a score is an opinion, and
   "this sentence could sit on any competitor's page" is a sentence somebody can
   go and replace. */
import { useState } from 'react';
import { Button } from '@/components/ui';
import { Icon } from '@/lib/icons';
import type { PageFacts, PageScoreResult } from '@/data/client';

const OK = 'var(--ok, #15803d)';
const WARN = 'var(--warn, #b45309)';
const BAD = 'var(--danger, #b91c1c)';
const band = (n: number) => (n >= 7 ? OK : n >= 4 ? WARN : BAD);

const AXES = ['effort', 'originality', 'skill', 'accuracy'] as const;

export interface ScoreEffort {
  scores: Record<string, number>;
  reasons: Record<string, string>;
  topic?: string;
  generic?: string[];
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: 10, fontSize: 13, padding: '3px 0' }}>
      <span className="dash-muted">{label}</span>
      <span>{children}</span>
    </div>
  );
}

const Mark = ({ on, good = true }: { on: boolean; good?: boolean }) => (
  <span style={{ color: on === good ? OK : WARN, fontWeight: 600 }}>{on ? 'yes' : 'no'}</span>
);

export function ScoreDetail({ effort, facts, proof, accuracyConcern, defaultOpen = false }: {
  effort: ScoreEffort | null;
  facts: PageFacts | null;
  proof?: PageScoreResult['proof'];
  accuracyConcern?: boolean;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [showFacts, setShowFacts] = useState(false);
  if (!effort) return null;

  return (
    <div>
      <button type="button" onClick={() => setOpen((v) => !v)}
        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
        <Icon name={open ? 'chevron-down' : 'chevron-right'} size={13} /> Why it scored this
      </button>

      {open && (
        <div style={{ marginTop: 8 }}>
          {/* A wrong page is a different and louder problem than a thin one, so
              it is said out loud rather than left in a row of numbers. */}
          {accuracyConcern && (
            <p style={{ color: BAD, fontSize: 13, fontWeight: 600, margin: '0 0 8px' }}>
              Accuracy scored {effort.scores.accuracy} — this page may be making claims that are wrong.
            </p>
          )}

          <div style={{ display: 'grid', gap: 8 }}>
            {AXES.map((a) => (
              <div key={a} style={{ display: 'grid', gridTemplateColumns: '92px 34px 1fr', gap: 10, alignItems: 'baseline' }}>
                <span style={{ textTransform: 'capitalize', fontWeight: 600, fontSize: 13 }}>{a}</span>
                <span style={{ fontWeight: 700, color: band(effort.scores[a] ?? 0) }}>{effort.scores[a] ?? '—'}</span>
                <span className="dash-muted" style={{ fontSize: 13 }}>{effort.reasons?.[a]}</span>
              </div>
            ))}
          </div>

          {(effort.generic?.length ?? 0) > 0 && (
            <div style={{ marginTop: 14 }}>
              <p style={{ fontWeight: 600, margin: '0 0 6px', fontSize: 13 }}>Lines any competitor could have written</p>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {effort.generic!.map((g, i) => (
                  <li key={i} className="dash-muted" style={{ fontSize: 13, margin: '3px 0' }}>&ldquo;{g}&rdquo;</li>
                ))}
              </ul>
            </div>
          )}

          {proof && (
            <div style={{ marginTop: 14 }}>
              <p style={{ fontWeight: 600, margin: '0 0 2px', fontSize: 13 }}>Proof on this page</p>
              <Row label="Own photos">
                <strong style={{ color: proof.photos.onPage ? OK : WARN }}>{proof.photos.onPage}</strong>
                {proof.photos.available > 0 && (
                  <span className="dash-muted"> · {proof.photos.available} in Expert Studio for this service</span>
                )}
              </Row>
              {proof.photos.stock > 0 && (
                <Row label="Stock photos">
                  <strong style={{ color: WARN }}>{proof.photos.stock}</strong>
                  <span className="dash-muted"> · a contractor page is the wrong place for these</span>
                </Row>
              )}
              <Row label="Reviews quoted">
                <strong style={{ color: proof.reviews.quoted ? OK : WARN }}>{proof.reviews.quoted}</strong>
                <span className="dash-muted">
                  {' '}· {proof.reviews.matching} of {proof.reviews.available} mention
                  {' '}{proof.reviews.terms.length ? proof.reviews.terms.map((t) => `“${t}”`).join(', ') : 'this service'}
                </span>
              </Row>
            </div>
          )}

          {facts && (
            <div style={{ marginTop: 10 }}>
              <Button variant="ghost" size="sm" onClick={() => setShowFacts((v) => !v)}>
                {showFacts ? 'Hide' : 'Show'} what&rsquo;s on the page
              </Button>
              {showFacts && (
                <div style={{ marginTop: 8 }}>
                  {facts.canonicalOffHost && (
                    <p style={{ color: BAD, fontSize: 13, fontWeight: 600, margin: '0 0 8px' }}>
                      Canonical points at another host: {facts.canonical}
                    </p>
                  )}
                  <Row label="Byline"><Mark on={facts.byline} /></Row>
                  <Row label="Schema">
                    {facts.schemaTypes.length ? facts.schemaTypes.join(', ') : <span className="dash-muted">none found</span>}
                  </Row>
                  <Row label="Business schema"><Mark on={facts.hasLocalBusinessSchema} /></Row>
                  <Row label="Images">
                    {facts.images.total} total · {facts.images.own} own · {facts.images.stock} stock · {facts.images.withAlt} with alt
                  </Row>
                  <Row label="Links">
                    {facts.links.internal} internal · {facts.links.external} external · {facts.links.authority} to .gov/.edu
                  </Row>
                  <Row label="Question headings">{facts.questionHeadings} of {facts.h2.length} H2s</Row>
                  <Row label="Specificity">{facts.numbers} numbers · {facts.numbersPer1k} per 1,000 words</Row>
                  <Row label="Trust markers">
                    licensed <Mark on={facts.trust.licensed} /> · insured <Mark on={facts.trust.insured} /> ·
                    {' '}bonded <Mark on={facts.trust.bonded} /> · warranty <Mark on={facts.trust.warranty} />
                  </Row>
                  {facts.keyword && (
                    <Row label={`"${facts.keyword.target}"`}>
                      title <Mark on={facts.keyword.inTitle} /> · H1 <Mark on={facts.keyword.inH1} /> ·
                      {' '}first 100 words <Mark on={facts.keyword.inFirst100} /> · an H2 <Mark on={facts.keyword.inAnyH2} />
                    </Row>
                  )}
                  <Row label="Meta description">
                    {facts.metaDescription ? `${facts.metaDescription.length} chars` : <span style={{ color: WARN }}>missing</span>}
                  </Row>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
